import { ArrowRight, Info, Loader2 } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'
import { TargetMarketField } from '@/components/TargetMarketField'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { usePerformanceOptions } from '@/lib/performanceQueries'
import {
  findGlobalTargetMarket,
  findTargetMarketLabel,
  useTargetMarketCatalog,
} from '@/lib/targetMarkets'
import { useAuth } from '@/provider/useAuth'
import { useFetcher } from '@/provider/useFetcher'
import type { RunFormProps } from './RunForm.types'
import { SuggestedInput } from './SuggestedInput'

export type { RunFormValues } from './RunForm.types'

const SECTION_LABEL = 'text-xs uppercase tracking-wider text-muted-foreground'

// Shared brand/category/market run form behind both the Landing and Schedules flows.
export function RunForm({
  idPrefix,
  pending,
  submitLabel,
  pendingLabel,
  helperText,
  modeControl,
  runDetail,
  header,
  className,
  onSubmit,
  resetSignal,
}: RunFormProps) {
  const fetcher = useFetcher()
  const catalogQuery = useTargetMarketCatalog(fetcher)
  const [brand, setBrand] = useState('')
  const [category, setCategory] = useState('')
  const [targetMarketIds, setTargetMarketIds] = useState<string[]>([])
  const initializedTargetMarkets = useRef(false)
  const [liveSearch, setLiveSearch] = useState(false)
  // Anonymous callers get a 401 on liveSearch, same boundary as intent_map.
  const signedIn = useAuth() !== null
  const performanceOptions = usePerformanceOptions()
  const brands = performanceOptions.data?.brands ?? []
  const brandSuggestions = useMemo(
    () => brands.map((option) => option.label),
    [brands]
  )
  const selectedBrand = brands.find(
    (option) =>
      option.label.toLocaleLowerCase() === brand.trim().toLocaleLowerCase()
  )
  const categorySuggestions = useMemo(() => {
    const categories = selectedBrand
      ? selectedBrand.categories
      : brands.flatMap((option) => option.categories)
    const seen = new Set<string>()

    return categories
      .map((option) => option.label)
      .filter((label) => {
        const key = label.toLocaleLowerCase()
        if (seen.has(key)) return false

        seen.add(key)
        return true
      })
  }, [brands, selectedBrand])
  const globalMarket = catalogQuery.data
    ? findGlobalTargetMarket(catalogQuery.data)
    : undefined

  useEffect(() => {
    if (initializedTargetMarkets.current || !globalMarket) {
      return
    }

    initializedTargetMarkets.current = true

    setTargetMarketIds([globalMarket.id])
  }, [globalMarket])

  // Owner bumps resetSignal after a successful submit to clear the fields.
  const [lastReset, setLastReset] = useState(resetSignal)

  useEffect(() => {
    if (resetSignal === lastReset) {
      return
    }

    setLastReset(resetSignal)
    setBrand('')
    setCategory('')
    setTargetMarketIds(globalMarket ? [globalMarket.id] : [])
    setLiveSearch(false)
  }, [globalMarket, lastReset, resetSignal])

  function handleSubmit() {
    if (!brand.trim() || !category.trim()) {
      toast.error('Enter both your brand and a category.')

      return
    }

    const submittedTargetMarketIds =
      targetMarketIds.length === 0 && globalMarket
        ? [globalMarket.id]
        : targetMarketIds
    const targetMarketLabel = catalogQuery.data
      ? (findTargetMarketLabel(catalogQuery.data, submittedTargetMarketIds) ??
        '')
      : ''

    onSubmit({
      brand: brand.trim(),
      category: category.trim(),
      targetMarketIds: submittedTargetMarketIds,
      targetMarketLabel,
      liveSearch: signedIn && liveSearch,
    })
  }

  const brandId = `${idPrefix}-brand`
  const categoryId = `${idPrefix}-category`
  const targetMarketsId = `${idPrefix}-target-markets`
  const liveSearchId = `${idPrefix}-live-search`
  const liveSearchLabelId = `${liveSearchId}-label`

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        handleSubmit()
      }}
      className={className}
    >
      {header}

      <fieldset className="space-y-3">
        <legend className={SECTION_LABEL}>What to measure</legend>

        <div className="grid gap-3 sm:grid-cols-2">
          <SuggestedInput
            id={brandId}
            label="Your brand"
            value={brand}
            suggestions={brandSuggestions}
            placeholder="Allbirds"
            disabled={pending}
            onValueChange={setBrand}
          />

          <div className="space-y-2">
            <Label htmlFor={targetMarketsId}>Target markets</Label>
            <TargetMarketField
              id={targetMarketsId}
              catalog={catalogQuery.data}
              isLoading={catalogQuery.isPending}
              isError={catalogQuery.isError}
              value={targetMarketIds}
              onValueChange={setTargetMarketIds}
              disabled={pending}
            />
          </div>
        </div>

        <div>
          <SuggestedInput
            id={categoryId}
            label="Product or category to track"
            value={category}
            suggestions={categorySuggestions}
            placeholder="trail running shoes for first-time marathoners"
            describedBy={`${categoryId}-guidance`}
            disabled={pending}
            onValueChange={setCategory}
          />
          <p
            id={`${categoryId}-guidance`}
            className="mt-2 text-xs text-muted-foreground"
          >
            Include the product, audience, or use case. Specific inputs produce
            better queries.
          </p>
        </div>
      </fieldset>

      <fieldset className="space-y-3">
        <legend className={SECTION_LABEL}>How to run</legend>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-6">
          {modeControl}

          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <Switch
              id={liveSearchId}
              aria-labelledby={liveSearchLabelId}
              checked={liveSearch}
              onCheckedChange={setLiveSearch}
              disabled={pending || !signedIn}
            />
            <Label
              id={liveSearchLabelId}
              htmlFor={liveSearchId}
              className="cursor-pointer"
            >
              Live search
            </Label>
            <TooltipProvider delayDuration={150}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    aria-label="About live search"
                    className="rounded-full text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-ring/50 focus-visible:ring-[3px]"
                  >
                    <Info className="size-3.5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent className="max-w-64 leading-relaxed">
                  Models search the web before answering. Uses part of your
                  monthly live-search allowance.
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            {!signedIn && (
              <p className="text-xs text-muted-foreground">
                Sign in to use live search.
              </p>
            )}
          </div>
        </div>

        {runDetail}
      </fieldset>

      <Button
        type="submit"
        className="group w-full py-6 text-base hover:opacity-95"
        disabled={pending || catalogQuery.isPending || !catalogQuery.data}
      >
        {pending ? (
          <>
            <Loader2 className="size-4 animate-spin" /> {pendingLabel}
          </>
        ) : (
          <>
            {submitLabel}{' '}
            <ArrowRight className="size-4 transition-transform group-hover:translate-x-0.5" />
          </>
        )}
      </Button>
      <p className="text-center text-xs text-muted-foreground">{helperText}</p>
    </form>
  )
}
